<template>
    <div
        v-if="shouldRender()"
        class="MaintenanceNotification"
        :class="{ 'is-overview-page': isOverviewPage, 'is-expanded': isExpanded }"
    >
        <div class="MaintenanceNotification-header" @click="toggleExpanded">
            <div class="MaintenanceNotification-content">
                <i class="ri-tools-line"></i>

                <template v-if="isOverviewPage">
                    <span class="MaintenanceNotification-text">
                        {{ notification.message }}
                    </span>
                </template>
                <template v-else>
                    <span class="MaintenanceNotification-text" v-if="isExpanded">
                        {{ notification.message }}
                    </span>
                </template>

                <i
                    class="MaintenanceNotification-toggle ri-arrow-down-s-line"
                    :class="{ 'is-expanded': isExpanded }"
                ></i>
            </div>
        </div>

        <div v-if="isExpanded" class="MaintenanceNotification-details">
            <div class="MaintenanceNotification-detail-item">
                <span class="detail-label">Geplante Wartung:</span>
                <span class="detail-value">{{ formattedStartTime }}</span>
            </div>
            <div class="MaintenanceNotification-detail-item">
                <span class="detail-label">Erwartetes Ende:</span>
                <span class="detail-value">{{ formattedEndTime }}</span>
            </div>
            <div class="MaintenanceNotification-detail-item">
                <span class="detail-label">Zeit bis zur Wartung:</span>
                <span class="detail-value">
                    <span v-if="isCheckingStatus" class="checking-status">
                        <i class="ri-loader-4-line"></i>
                        Überprüfe Status...
                    </span>
                    <span v-else-if="isMaintenanceTimePassed" class="maintenance-passed">
                        {{ activeMaintenanceMessage }}
                    </span>
                    <span v-else>{{ timeUntilMaintenance }}</span>
                </span>
            </div>
        </div>
    </div>
</template>

<script>
import { format, differenceInHours, differenceInMinutes } from 'date-fns';
import { de } from 'date-fns/locale';
import { maintenanceService } from '@/services/axiosConfig';

export default {
    name: 'MaintenanceNotification',
    props: {
        notification: {
            type: Object,
            default: null,
        },
        isOverviewPage: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            isExpanded: false,
            isCheckingStatus: false,
        };
    },

    mounted() {
        if (this.isOverviewPage) {
            this.isExpanded = true;
        }
    },
    computed: {
        formattedTime() {
            if (!this.notification?.scheduledDate?.startDate) return '';

            const date = new Date(this.notification.scheduledDate.startDate);
            return format(date, 'dd.MM.yyyy HH:mm', { locale: de });
        },
        formattedStartTime() {
            if (!this.notification?.scheduledDate?.startDate) return '';

            const date = new Date(this.notification.scheduledDate.startDate);
            return format(date, 'dd.MM.yyyy HH:mm', { locale: de });
        },
        formattedEndTime() {
            if (this.notification?.scheduledDate?.endDate) {
                const date = new Date(this.notification.scheduledDate.endDate);
                return format(date, 'dd.MM.yyyy HH:mm', { locale: de });
            }
            return 'Nicht verfügbar';
        },
        timeUntilMaintenance() {
            if (!this.notification?.scheduledDate?.startDate) return '';

            const now = new Date();
            const maintenanceStart = new Date(this.notification.scheduledDate.startDate);
            const maintenanceEnd = this.notification.scheduledDate.endDate
                ? new Date(this.notification.scheduledDate.endDate)
                : null;

            if (maintenanceEnd && now > maintenanceEnd) {
                this.checkMaintenanceFinished();
                return 'Überprüfe Status...';
            }

            if (maintenanceEnd && now >= maintenanceStart && now <= maintenanceEnd) {
                return 'Wartung läuft - System ist vorübergehend nicht verfügbar';
            }

            if (!maintenanceEnd && now >= maintenanceStart) {
                return 'Wartung läuft - System ist vorübergehend nicht verfügbar';
            }

            const totalMinutes = differenceInMinutes(maintenanceStart, now);

            if (totalMinutes <= 0) {
                return 'Wartung läuft - System ist vorübergehend nicht verfügbar';
            }

            const hours = Math.floor(totalMinutes / 60);
            const minutes = totalMinutes % 60;

            if (hours > 24) {
                const days = Math.floor(hours / 24);
                const remainingHours = hours % 24;
                return `${days} Tag${days > 1 ? 'e' : ''}, ${remainingHours} Stunde${
                    remainingHours > 1 ? 'n' : ''
                }`;
            } else if (hours > 0) {
                return `${hours} Stunde${hours > 1 ? 'n' : ''}, ${minutes} Minute${
                    minutes > 1 ? 'n' : ''
                }`;
            } else {
                return `${minutes} Minute${minutes > 1 ? 'n' : ''}`;
            }
        },
        isMaintenanceTimePassed() {
            if (!this.notification?.scheduledDate?.startDate) return false;

            const now = new Date();
            const maintenanceStart = new Date(this.notification.scheduledDate.startDate);
            const maintenanceEnd = this.notification.scheduledDate.endDate
                ? new Date(this.notification.scheduledDate.endDate)
                : null;

            if (maintenanceEnd && now > maintenanceEnd) {
                return false;
            }

            if (maintenanceEnd && now >= maintenanceStart && now <= maintenanceEnd) {
                return true;
            }

            if (!maintenanceEnd && now >= maintenanceStart) {
                return true;
            }

            return false;
        },
        isFutureMaintenance() {
            if (!this.notification?.scheduledDate?.startDate) {
                return false;
            }

            const now = new Date();
            const maintenanceStart = new Date(this.notification.scheduledDate.startDate);

            return now < maintenanceStart;
        },
        activeMaintenanceMessage() {
            return 'Wartung läuft - System ist vorübergehend nicht verfügbar';
        },
    },
    methods: {
        toggleExpanded() {
            this.isExpanded = !this.isExpanded;
        },

        shouldRender() {
            return this.notification && this.isFutureMaintenance;
        },

        async checkMaintenanceFinished() {
            if (this.isCheckingStatus) return;

            this.isCheckingStatus = true;

            try {
                const isFinished = await maintenanceService.checkMaintenanceFinished();

                if (isFinished) {
                    this.$emit('maintenance-finished');
                }
            } catch (error) {
            } finally {
                this.isCheckingStatus = false;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.MaintenanceNotification {
    position: absolute;
    top: 12px;
    right: 12px;
    z-index: 900;
    background: var(--color-orange-dark);
    color: white;
    border-radius: 8px;
    font-size: 0.875rem;
    font-weight: 500;
    max-width: 350px;
    animation: slideIn 0.3s ease-out;
    overflow: hidden;

    &:not(.is-overview-page):not(.is-expanded) {
        max-width: 40px;
        min-width: 40px;
        width: 40px;
        height: 40px;
        display: flex;
        align-items: center;
        top: 0;
        right: 0;
        border-top-right-radius: 0;
        border-top-left-radius: 0;
        border-bottom-right-radius: 0;

        justify-content: center;
        transition: all 0.3s ease;

        .MaintenanceNotification-content {
            justify-content: center;
            gap: 0;
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
        }

        .MaintenanceNotification-text,
        .MaintenanceNotification-time,
        .MaintenanceNotification-toggle {
            display: none;
        }

        i.ri-tools-line {
            font-size: 0.875rem;
            margin: 0;
        }
    }

    &-header {
        cursor: pointer;
        padding: 12px 16px;
        transition: background-color 0.2s ease;

        &:hover {
            background: rgba(255, 255, 255, 0.1);
        }
    }

    &-content {
        display: flex;
        align-items: center;
        gap: 8px;
    }

    &-text {
        flex: 1;
    }

    &-time {
        font-size: 0.75rem;
        opacity: 0.9;
        white-space: nowrap;
    }

    &-toggle {
        font-size: 1rem;
        flex-shrink: 0;
        transition: transform 0.2s ease;
        opacity: 0.8;

        &.is-expanded {
            transform: rotate(180deg);
        }
    }

    &-details {
        background: rgba(0, 0, 0, 0.1);
        padding: 12px 16px;
        border-top: 0.5px solid rgba(255, 255, 255, 0.2);
        animation: slideDown 0.3s ease-out;
    }

    &-detail-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 8px;
        font-size: 0.8rem;

        &:last-child {
            margin-bottom: 0;
        }

        .detail-label {
            opacity: 0.9;
            font-weight: 500;
        }

        .detail-value {
            font-weight: 600;
            text-align: right;

            .checking-status {
                display: flex;
                align-items: center;
                gap: 4px;
                color: rgba(255, 255, 255, 0.9);

                i {
                    animation: spin 1s linear infinite;
                    font-size: 0.8rem;
                }
            }

            .maintenance-passed {
                color: rgba(255, 255, 255, 0.8);
                font-style: italic;
            }
        }
    }

    i {
        font-size: 1rem;
        flex-shrink: 0;
    }

    @keyframes slideIn {
        from {
            transform: translateX(100%);
            opacity: 0;
        }
        to {
            transform: translateX(0);
            opacity: 1;
        }
    }

    @keyframes slideDown {
        from {
            max-height: 0;
            opacity: 0;
        }
        to {
            max-height: 200px;
            opacity: 1;
        }
    }

    @keyframes spin {
        from {
            transform: rotate(0deg);
        }
        to {
            transform: rotate(360deg);
        }
    }

    @media (max-width: 768px) {
        top: 8px;
        right: 8px;
        left: 8px;
        max-width: none;
        font-size: 0.8rem;

        &-header {
            padding: 10px 12px;
        }

        &-details {
            padding: 10px 12px;
        }

        &-time {
            font-size: 0.7rem;
        }

        &-detail-item {
            font-size: 0.75rem;
        }
    }
}
</style>
